import React, { useState } from 'react';
import { 
  AlertTriangle, Search, Filter, ChevronDown, ChevronUp, Check, 
  X, MapPin, Clock, MessageSquare, CheckCircle, Phone, Bell, 
  ArrowDown, ArrowUp, Users
} from 'lucide-react';
import { SafetyAlert, SafetyStatus, SafetySeverity, SafetyAlertType } from './types';

interface ActiveAlertsTabProps {
  alerts: SafetyAlert[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  filterBy: {
    severity: SafetySeverity[];
    location: string[];
    status: SafetyStatus[];
    type: SafetyAlertType[];
  };
  toggleSeverityFilter: (severity: SafetySeverity) => void;
  toggleLocationFilter: (location: string) => void;
  toggleStatusFilter: (status: SafetyStatus) => void;
  toggleTypeFilter: (type: SafetyAlertType) => void;
  clearFilters: () => void;
  sortBy: 'timestamp' | 'severity' | 'location';
  sortDirection: 'asc' | 'desc';
  onSetSortBy: (field: 'timestamp' | 'severity' | 'location') => void;
  onAcknowledgeAlert: (alertId: string) => void;
  onResolveAlert: (alertId: string, notes?: string) => void;
  onSendToCaregiverPortal: (alertId: string, note: string) => void;
}

const ActiveAlertsTab: React.FC<ActiveAlertsTabProps> = ({
  alerts,
  searchTerm,
  setSearchTerm,
  showFilters,
  setShowFilters,
  filterBy,
  toggleSeverityFilter,
  toggleLocationFilter,
  toggleStatusFilter,
  toggleTypeFilter,
  clearFilters,
  sortBy,
  sortDirection,
  onSetSortBy,
  onAcknowledgeAlert,
  onResolveAlert,
  onSendToCaregiverPortal
}) => {
  // UI state for selected alert and forms
  const [selectedAlert, setSelectedAlert] = useState<SafetyAlert | null>(null);
  const [resolutionNote, setResolutionNote] = useState<string>('');
  const [caregiverNote, setCaregiverNote] = useState<string>('');
  
  // Get all unique locations for the filter dropdown
  const uniqueLocations = Array.from(new Set(alerts.map(alert => alert.location.room)));
  
  // Utility functions
  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };
  
  const getStatusColor = (status: SafetyStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-red-500';
      case 'ACKNOWLEDGED':
        return 'text-yellow-500';
      case 'RESOLVED':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };
  
  const getStatusBadge = (status: SafetyStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-red-100 text-red-800';
      case 'ACKNOWLEDGED':
        return 'bg-yellow-100 text-yellow-800';
      case 'RESOLVED':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getSeverityColor = (severity: SafetySeverity) => {
    switch (severity) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'LOW':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };
  
  const getSeverityBadge = (severity: SafetySeverity) => {
    switch (severity) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getAlertTypeIcon = (type: SafetyAlertType) => {
    switch (type) {
      case 'FALL_RISK':
        return <AlertTriangle className="text-red-500" size={18} />;
      case 'WANDERING':
        return <MapPin className="text-blue-500" size={18} />;
      case 'ENVIRONMENTAL':
        return <Bell className="text-purple-500" size={18} />;
      case 'BEHAVIORAL':
        return <Users className="text-green-500" size={18} />;
      default:
        return <AlertTriangle className="text-gray-500" size={18} />;
    }
  };
  
  // Handler for submitting resolution
  const handleResolveAlert = () => {
    if (selectedAlert) {
      onResolveAlert(selectedAlert.id, resolutionNote);
      setResolutionNote('');
      setSelectedAlert(null);
    }
  };
  
  // Handler for sending to caregiver portal
  const handleSendToCaregiverPortal = () => {
    if (selectedAlert && caregiverNote.trim() !== '') {
      onSendToCaregiverPortal(selectedAlert.id, caregiverNote);
      setCaregiverNote('');
    }
  };
  
  return (
    <div>
      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          {/* Search */}
          <div className="relative mb-4 md:mb-0 w-full md:w-auto md:flex-grow md:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search alerts by description or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Filter Toggle */}
          <div className="flex space-x-2">
            <button
              className={`flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                showFilters ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300'
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {
                (filterBy.severity.length > 0 || 
                 filterBy.location.length > 0 || 
                 filterBy.status.length > 0 ||
                 filterBy.type.length > 0) && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                    {filterBy.severity.length + filterBy.location.length + filterBy.status.length + filterBy.type.length}
                  </span>
                )
              }
            </button>
            
            {/* Sort Dropdown */}
            <div className="relative inline-block text-left">
              <button
                className="flex items-center px-4 py-2 border rounded-md text-sm font-medium bg-white text-gray-700 border-gray-300"
                onClick={() => {
                  // Cycle through sort options: timestamp -> severity -> location -> timestamp
                  if (sortBy === 'timestamp') onSetSortBy('severity');
                  else if (sortBy === 'severity') onSetSortBy('location');
                  else onSetSortBy('timestamp');
                }}
              >
                Sort: {sortBy === 'timestamp' ? 'Time' : sortBy === 'severity' ? 'Severity' : 'Location'}
                {sortDirection === 'asc' ? (
                  <ArrowUp className="h-4 w-4 ml-2" />
                ) : (
                  <ArrowDown className="h-4 w-4 ml-2" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Severity Filters */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Severity</h4>
                <div className="space-y-2">
                  {(['HIGH', 'MEDIUM', 'LOW'] as SafetySeverity[]).map(severity => (
                    <div key={severity} className="flex items-center">
                      <button
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium mr-2 ${
                          filterBy.severity.includes(severity) ? getSeverityBadge(severity) : 'bg-gray-100 text-gray-700'
                        }`}
                        onClick={() => toggleSeverityFilter(severity)}
                      >
                        {filterBy.severity.includes(severity) && <Check className="h-3 w-3 mr-1" />}
                        {severity}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Status Filters */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                <div className="space-y-2">
                  {(['ACTIVE', 'ACKNOWLEDGED', 'RESOLVED'] as SafetyStatus[]).map(status => (
                    <div key={status} className="flex items-center">
                      <button
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium mr-2 ${
                          filterBy.status.includes(status) ? getStatusBadge(status) : 'bg-gray-100 text-gray-700'
                        }`}
                        onClick={() => toggleStatusFilter(status)}
                      >
                        {filterBy.status.includes(status) && <Check className="h-3 w-3 mr-1" />}
                        {status}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Location Filters */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Location</h4>
                <div className="space-y-2 max-h-24 overflow-y-auto">
                  {uniqueLocations.map(location => (
                    <div key={location} className="flex items-center">
                      <button
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium mr-2 ${
                          filterBy.location.includes(location) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}
                        onClick={() => toggleLocationFilter(location)}
                      >
                        {filterBy.location.includes(location) && <Check className="h-3 w-3 mr-1" />}
                        {location}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Alert Type Filters */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Alert Type</h4>
                <div className="space-y-2">
                  {(['FALL_RISK', 'WANDERING', 'ENVIRONMENTAL', 'BEHAVIORAL'] as SafetyAlertType[]).map(type => (
                    <div key={type} className="flex items-center">
                      <button
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium mr-2 ${
                          filterBy.type.includes(type) ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                        }`}
                        onClick={() => toggleTypeFilter(type)}
                      >
                        {filterBy.type.includes(type) && <Check className="h-3 w-3 mr-1" />}
                        {type.replace('_', ' ')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {(filterBy.severity.length > 0 || 
              filterBy.location.length > 0 || 
              filterBy.status.length > 0 ||
              filterBy.type.length > 0) && (
              <div className="mt-4 flex justify-end">
                <button
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Grid/List View of Alerts */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {alerts.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {alerts.map(alert => (
              <div 
                key={alert.id} 
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${selectedAlert?.id === alert.id ? 'bg-blue-50' : ''}`}
                onClick={() => setSelectedAlert(selectedAlert?.id === alert.id ? null : alert)}
              >
                <div className="sm:flex sm:items-start sm:justify-between">
                  {/* Alert Info */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {getAlertTypeIcon(alert.type)}
                    </div>
                    <div className="ml-3">
                      <div className="flex items-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(alert.severity)}`}>
                          {alert.severity}
                        </span>
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(alert.status)}`}>
                          {alert.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-medium text-gray-900">{alert.description}</p>
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <MapPin className="h-3 w-3 mr-1" />
                        <span>{alert.location.room} ({alert.location.zone})</span>
                        <span className="mx-2">•</span>
                        <Clock className="h-3 w-3 mr-1" />
                        <span>{formatTimestamp(alert.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons (for larger screens) */}
                  <div className="mt-4 sm:mt-0 sm:ml-6 sm:flex-shrink-0 flex space-x-2 sm:space-x-3">
                    {alert.status === 'ACTIVE' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAcknowledgeAlert(alert.id);
                        }}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Acknowledge
                      </button>
                    )}
                    {alert.status !== 'RESOLVED' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAlert(alert);
                        }}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200"
                      >
                        Resolve
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAlert(alert);
                      }}
                      className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                        selectedAlert?.id === alert.id
                          ? 'bg-gray-200 text-gray-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {selectedAlert?.id === alert.id ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          View Details
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Expanded Alert Details */}
                {selectedAlert?.id === alert.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column: Details & Response */}
                      <div>
                        {/* Alert Details */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Alert Details</h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <dt className="text-gray-500">Type</dt>
                            <dd className="text-gray-900">{alert.type.replace('_', ' ')}</dd>
                            
                            <dt className="text-gray-500">Severity</dt>
                            <dd className="text-gray-900">{alert.severity}</dd>
                            
                            <dt className="text-gray-500">Status</dt>
                            <dd className="text-gray-900">{alert.status}</dd>
                            
                            <dt className="text-gray-500">Location</dt>
                            <dd className="text-gray-900">{alert.location.room} ({alert.location.zone})</dd>
                            
                            <dt className="text-gray-500">Time</dt>
                            <dd className="text-gray-900">{formatTimestamp(alert.timestamp)}</dd>
                          </dl>
                        </div>
                        
                        {/* Risk Factors */}
                        {alert.riskFactors && alert.riskFactors.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Risk Factors</h4>
                            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                              {alert.riskFactors.map((factor, index) => (
                                <li key={index}>{factor}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Response Information */}
                        {alert.response && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Response Information</h4>
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <dt className="text-gray-500">Action</dt>
                              <dd className="text-gray-900">{alert.response.action}</dd>
                              
                              <dt className="text-gray-500">Responder</dt>
                              <dd className="text-gray-900">{alert.response.responder}</dd>
                              
                              <dt className="text-gray-500">Response Time</dt>
                              <dd className="text-gray-900">{formatTimestamp(alert.response.time)}</dd>
                            </dl>
                          </div>
                        )}
                      </div>
                      
                      {/* Right Column: Actions & Notes */}
                      <div>
                        {/* Caregiver Notes */}
                        {alert.caregiverNotes && alert.caregiverNotes.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Caregiver Notes</h4>
                            <div className="space-y-2">
                              {alert.caregiverNotes.map((note, index) => (
                                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                  <p className="text-sm text-gray-700">{note.content}</p>
                                  <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                                    <span>{note.author}</span>
                                    <span>{formatTimestamp(note.timestamp)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Resolution Form (if alert is not resolved) */}
                        {alert.status !== 'RESOLVED' && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Resolve Alert</h4>
                            <div className="space-y-2">
                              <textarea
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                rows={3}
                                placeholder="Add resolution details..."
                                value={resolutionNote}
                                onChange={(e) => setResolutionNote(e.target.value)}
                              />
                              <button
                                className="w-full flex justify-center items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                                onClick={handleResolveAlert}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark as Resolved
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Send to Caregiver Portal */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Send to Caregiver Portal</h4>
                          <div className="space-y-2">
                            <textarea
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              rows={3}
                              placeholder="Add note for caregivers..."
                              value={caregiverNote}
                              onChange={(e) => setCaregiverNote(e.target.value)}
                            />
                            <div className="flex space-x-2">
                              <button
                                className="flex-grow flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                                onClick={handleSendToCaregiverPortal}
                                disabled={!caregiverNote.trim()}
                              >
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Send to Caregiver Portal
                              </button>
                              <button
                                className="flex justify-center items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
                              >
                                <Phone className="h-4 w-4 mr-2" />
                                Call Caregiver
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts found</h3>
            <p className="text-gray-500">
              {searchTerm || 
               filterBy.severity.length > 0 || 
               filterBy.location.length > 0 || 
               filterBy.status.length > 0 ||
               filterBy.type.length > 0 
                ? 'Try adjusting your search or filters'
                : 'No alerts to display at this time'}
            </p>
            {(searchTerm || 
              filterBy.severity.length > 0 || 
              filterBy.location.length > 0 || 
              filterBy.status.length > 0 ||
              filterBy.type.length > 0) && (
              <button
                className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveAlertsTab; 